import React, { useState } from "react";
import { Box, FormControl, InputLabel, Select, MenuItem, Typography, Button, Paper } from "@mui/material";
import { MOCK_PERSONAS } from "./mockData";
import StudioDashboard from "../pages/Account/Studio/StudioDashboard";
import OrderDetailsStudio from "../pages/Account/Studio/OrderDetailsStudio";
import { ArrowBack } from "@mui/icons-material";

// A mini-router for the preview experience
type View = "DASHBOARD" | "ORDER_DETAILS";

const StudioDevPreview: React.FC = () => {
  const [currentPersonaKey, setCurrentPersonaKey] = useState<keyof typeof MOCK_PERSONAS>("SARAH_NEW");
  const [currentView, setCurrentView] = useState<View>("DASHBOARD");
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

  const persona = MOCK_PERSONAS[currentPersonaKey];

  // We need to intercept navigation actions from the components
  // Since we can't easily overwrite the `navigate` hook inside the child components without
  // rigorous refactoring, we will accept the limitation that 'clicking' a card might change the URL URL
  // which is annoying.
  // 
  // However, we can trick this by using the `manualOrders` prop we added. 
  // But Project Cards use `navigate` directly.
  
  // Actually, ProjectCards behave based on the `action` prop passed or computed.
  // If we want to test "View Order Details", we need to override the behavior.
  
  // Strategy: We will just verify the Dashboard for now. 
  // If the user wants to see Order Details, they select it from the top dropdown?
  // Let's add an "Inspect Order" dropdown too.

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#333", p: 2 }}>
      {/* Dev Controls */}
      <Paper sx={{ mb: 2, p: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
         <Typography variant="h6">🧙‍♂️ Studio Preview</Typography>
         
         <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Persona</InputLabel>
            <Select 
                value={currentPersonaKey} 
                label="Persona"
                onChange={(e) => {
                    setCurrentPersonaKey(e.target.value as any);
                    setCurrentView("DASHBOARD");
                    setSelectedOrderId(null);
                }}
            >
                {Object.entries(MOCK_PERSONAS).map(([key, p]) => (
                    <MenuItem key={key} value={key}>{p.name}</MenuItem>
                ))}
            </Select>
         </FormControl>

         <Box sx={{ flexGrow: 1 }} />

         {currentView === "DASHBOARD" && (
             <Typography variant="body2" color="text.secondary">
                 Clicking cards will attempt real navigation (and likely fail or hit 404). 
                 Use the dropdown below to preview specific order pages.
             </Typography>
         )}

         {currentView === "ORDER_DETAILS" && (
             <Button startIcon={<ArrowBack />} onClick={() => setCurrentView("DASHBOARD")}>
                 Back to Dashboard
             </Button>
         )}
      </Paper>

      {/* View Container */}
      <Paper sx={{ minHeight: '800px', overflow: 'hidden', borderRadius: 4, boxShadow: '0 0 20px rgba(0,0,0,0.5)' }}>
          {currentView === "DASHBOARD" ? (
              <Box sx={{ height: '100%' }}>
                  <StudioDashboard 
                    manualOrders={persona.orders}
                    onNavigate={(path) => {
                        console.log("Mock navigating to:", path);
                        // Extract Order ID if it's an order details link
                        if (path.includes('/orders/')) {
                            const id = path.split('/orders/')[1];
                            setSelectedOrderId(id);
                            setCurrentView("ORDER_DETAILS");
                        }
                        // For other links, we might just log or alert in this preview
                        else {
                            alert(`Navigation to ${path} intercepted in Preview Mode`);
                        }
                    }}
                  />
                  
                  {/* Manual Navigation Links for Testing */}
                  <Box sx={{ p: 4, bgcolor: '#f0f0f0', borderTop: '1px solid #ccc' }}>
                      <Typography variant="h6" gutterBottom>Debug Navigation</Typography>
                      {persona.orders.map(order => (
                          <Button 
                            key={order.paypalOrderId} 
                            variant="outlined" 
                            sx={{ mr: 1, mb: 1 }}
                            onClick={() => {
                                setSelectedOrderId(order.paypalOrderId);
                                setCurrentView("ORDER_DETAILS");
                            }}
                          >
                              Inspect Order {order.paypalOrderId}
                          </Button>
                      ))}
                  </Box>
              </Box>
          ) : (
              selectedOrderId && (
                  <OrderDetailsStudio 
                    manualOrder={persona.orders.find(o => o.paypalOrderId === selectedOrderId)} 
                  />
              )
          )}
      </Paper>
    </Box>
  );
};

export default StudioDevPreview;
